import classnames from 'classnames';
import { Data, Modal, Terms, UI, Utils as UtilsModule } from 'common';
import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect, useState } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import Utils from './Utils';

const {
	WrappedCheckboxWithLabel,
	WrappedInputTextWithLabel,
	WrappedButton,
	WrappedButtonType,
	Typography,
	Frame,
	Link,
	MobileAppQRCode,
} = UI;
const { Util, withLayoutAndModalContexts, Navigation, Session } = UtilsModule;
const { ModalContext } = Modal;
const { TermsDialog } = Terms;

const ActivationConfirmation = () => {
	const handleRedirect = () => Navigation.navigateTo('mail');
	const handleRedirectDownload = () => Navigation.navigateTo('WebRegDownload');
	const { APP_STORE_URL, GOOGLE_PLAY_URL } = Data.MobileAppUrls;

	// Confirmation
	return (
		<div className="account-users user-activation">
			<Frame className="confirmation-frame">
				<div className="confirmation-header">
					<Typography.H3 className="user-activation-created-account-title">
						Your account was created!
					</Typography.H3>
					<Typography.H4>Ready to get started?</Typography.H4>
					<Typography.P>
						Access Stamps.com from your PC or Mac using any major browser, or
						download our desktop app for PC and Mac.
					</Typography.P>
				</div>
				<WrappedButton
					label="Continue in Browser"
					onClick={handleRedirect}
					buttonType={WrappedButtonType.Primary}
					dataqa="user-activation-continue-in-browser"
				/>
				<WrappedButton
					label="Download our Desktop App"
					onClick={handleRedirectDownload}
					buttonType={WrappedButtonType.Secondary}
					dataqa="user-activation-download-desktop-app"
				/>
				<Frame className="mobile-download-frame">
					<MobileAppQRCode />
					<div className="mobile-app-download-text">
						<Typography.P className="bold">Mobile App</Typography.P>
						<Typography.P>
							Scan this QR code to download our mobile app or find it on the{' '}
							<Link
								href={APP_STORE_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="mobile-app-download-link"
								dataqa="app-store-link"
							>
								App Store
							</Link>
							{' and '}
							<Link
								href={GOOGLE_PLAY_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="mobile-app-download-link"
								dataqa="google-play-link"
							>
								Google Play
							</Link>
							.
						</Typography.P>
					</div>
				</Frame>
			</Frame>
		</div>
	);
};

const ActivationForm = ({
	activationCode,
	formData,
	setFormData,
	setUserActivated,
}) => {
	const { showModal, showError } = useContext(ModalContext);

	const [checked, setCheckBox] = useState(false);
	const [errorMessage, setErrorMessage] = useState({});

	const launchTermsConditionsModal = () => {
		showModal({
			component: TermsDialog,
			data: {
				termType: 'General',
			},
		});
	};

	const resultValidateForm = () => {
		const formRule = Utils.getFormRule();
		return Utils.validateData(formRule, formData);
	};

	const onInputChange = e => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: name === 'phone' ? Util.formatPhoneNumber(value) : value,
		});
	};

	const onBlur = (e, fieldName) => {
		const name = fieldName || e.target.name;
		const resultValidate = resultValidateForm();

		setErrorMessage({
			...errorMessage,
			[name]: resultValidate && resultValidate[name],
		});
	};

	const onCheckboxChange = e => {
		setCheckBox(e.checked);
	};

	const onSubmitClick = async () => {
		const { userId, userName, custId, phone, firstName, lastName, password } =
			formData;
		const validateResult = Utils.validateData(Utils.getFormRule(), formData);

		if (isEmpty(validateResult)) {
			setErrorMessage(validateResult);
			const response = await AccountCommunication.activateUser({
				UserId: userId,
				CustId: custId,
				Phone: Util.removePhoneFormatting(phone),
				UserName: userName,
				FirstName: firstName,
				LastName: lastName,
				ActivationCode: activationCode,
				NewPassword: password,
				Codeword1Type: 'MothersMaidenName', // Codewords removed. Using dummy data https://auctane.atlassian.net/browse/PPT-119
				Codeword2Type: 'PetsName',
				Codeword1: 'empty',
				Codeword2: 'empty',
				LegalTermAcceptanceInfo: {
					TermType: 1,
				},
				StampsId: Util.getQueryParamByName('stampsID'),
				MessageCode: Util.getQueryParamByName('hmac'),
			});
			if (AccountCommunication.isSuccessful(response)) {
				setUserActivated(true);
			} else {
				showError({ response });
			}
		} else {
			setErrorMessage(validateResult);
		}
	};

	const isEnableSubmitBtn = () => {
		const resultValidate = resultValidateForm();
		return checked && Object.keys(resultValidate).length === 0;
	};

	return (
		<div className="account-users user-activation">
			<Frame>
				<div className="p-col-12">
					<div className="brand-logo"></div>
					<Typography.H3>Create Your Account</Typography.H3>
					<div className="email-container">
						<Typography.HeadingLabel>Email</Typography.HeadingLabel>
						<Typography.P>{formData.emailAddress}</Typography.P>
					</div>
					<WrappedInputTextWithLabel
						isAdl
						label="Password:"
						className={classnames('wrapped-field', 'password')}
						uiElementConfig={{
							type: 'password',
							name: 'password',
							required: true,
							dataqa: 'password',
							onChange: onInputChange,
							value: formData.password,
							className: 'input-password',
							onBlur,
						}}
						errorMessage={errorMessage.password ? errorMessage.password[0] : ''}
						isInputInvalid={errorMessage.password}
					/>
					<WrappedInputTextWithLabel
						isAdl
						label="Confirm Password:"
						className={classnames('wrapped-field', 'confirmPassword')}
						uiElementConfig={{
							type: 'password',
							name: 'confirmPassword',
							required: true,
							dataqa: 'confirmPassword',
							onChange: onInputChange,
							value: formData.confirmPassword,
							className: 'input-confirmPassword',
							onBlur,
						}}
						errorMessage={
							errorMessage.confirmPassword
								? errorMessage.confirmPassword[0]
								: ''
						}
						isInputInvalid={errorMessage.confirmPassword}
					/>
					<div className="p-grid">
						<div className="p-col-6">
							<WrappedInputTextWithLabel
								isAdl
								label="First Name:"
								className={classnames('wrapped-field', 'firstName')}
								uiElementConfig={{
									type: 'text',
									name: 'firstName',
									maxLength: 40,
									required: true,
									dataqa: 'firstName',
									onChange: onInputChange,
									value: formData.firstName,
									className: 'input-firstName',
									onBlur,
								}}
								errorMessage={
									errorMessage.firstName ? errorMessage.firstName[0] : ''
								}
								isInputInvalid={errorMessage.firstName}
							/>
						</div>
						<div className="p-col-6">
							<WrappedInputTextWithLabel
								isAdl
								label="Last Name:"
								className={classnames('wrapped-field', 'lastName')}
								uiElementConfig={{
									type: 'text',
									name: 'lastName',
									maxLength: 40,
									required: true,
									dataqa: 'lastName',
									onChange: onInputChange,
									value: formData.lastName,
									className: 'input-lastName',
									onBlur,
								}}
								errorMessage={
									errorMessage.lastName ? errorMessage.lastName[0] : ''
								}
								isInputInvalid={errorMessage.lastName}
							/>
						</div>
					</div>
					<WrappedInputTextWithLabel
						isAdl
						label="Phone:"
						className={classnames('wrapped-field', 'phone')}
						uiElementConfig={{
							type: 'text',
							name: 'phone',
							maxLength: 23,
							required: true,
							dataqa: 'phone',
							onChange: onInputChange,
							value: formData.phone,
							className: 'input-phone',
							onBlur,
						}}
						errorMessage={errorMessage.phone ? errorMessage.phone[0] : ''}
						isInputInvalid={errorMessage.phone}
					/>
					<div className="term-conditions">
						<WrappedCheckboxWithLabel
							isAdl
							inputId="termConditions"
							uiElementConfig={{
								name: 'termConditions',
								children: (
									<span>
										I agree to the{' '}
										<Link
											className="link-button"
											onClick={launchTermsConditionsModal}
											dataqa="terms-conditions"
										>
											Terms & Conditions
										</Link>
									</span>
								),
								checked,
								onChange: onCheckboxChange,
								dataqa: 'user-activation-terms-and-conditions',
							}}
						/>
					</div>
					<WrappedButton
						className="continue-button"
						label="Activate Account"
						onClick={onSubmitClick}
						disabled={!isEnableSubmitBtn()}
						dataqa="user-activation-continue"
					/>
				</div>
			</Frame>
		</div>
	);
};

const UserActivation = () => {
	const stampsId = Util.getQueryParamByName('stampsID');
	const activationCode = Util.getQueryParamByName('activationCode');
	const messageCode = Util.getQueryParamByName('hmac');

	const { showError } = useContext(ModalContext);

	const [userActivated, setUserActivated] = useState(false);
	const [formData, setFormData] = useState({
		userId: '',
		custId: '',
		userName: '',
		firstName: '',
		lastName: '',
		phone: '',
		password: '',
		confirmPassword: '',
		emailAddress: '',
	});

	const fetchUserForActivation = async () => {
		const response = await AccountCommunication.getUserForActivation({
			stampsId,
			activationCode,
			messageCode,
		});

		if (AccountCommunication.isSuccessful(response)) {
			const {
				UserId,
				CustId,
				UserName,
				FirstName,
				LastName,
				PhoneNumber,
				EmailAddress,
			} = response.data;

			setFormData(prevData => ({
				...prevData,
				userId: UserId,
				custId: CustId,
				userName: UserName,
				firstName: FirstName,
				lastName: LastName,
				phone: Util.formatPhoneNumber(PhoneNumber),
				emailAddress: EmailAddress,
			}));
		} else {
			showError({ response });
		}
	};

	useEffect(() => {
		fetchUserForActivation();
	}, []);

	if (userActivated) {
		return <ActivationConfirmation />;
	}

	return (
		<ActivationForm
			activationCode={activationCode}
			formData={formData}
			setUserActivated={setUserActivated}
			setFormData={setFormData}
		/>
	);
};

export default withLayoutAndModalContexts(UserActivation);
